'use client';
import React from 'react';
import Image from '@repo/ui/src/components/Image';
import QuantitySelector from '@repo/ui/src/components/QuantitySelector';
import { useCurrencyString } from '@repo/ui/src/hooks/useCurrencyString';
import { classNames } from '@repo/ui/src/utils/classNames';
import type { CartLineItem as CartLineItemType } from '@repo/ui/src/types/cart';

interface CartLineItemProps {
  item: CartLineItemType;
  outOfStock?: boolean;
  isUpdating?: boolean;
  onQuantityChange: (id: string, newQuantity: number) => void;
  onRemove: (id: string) => void;
  outOfStockMessage?: string;
  removeLabel?: string;
  outOfStockLabel?: string;
  quantityLabel?: string;
  currency?: string;
}

const CartLineItem = ({
  item,
  outOfStock,
  isUpdating,
  onQuantityChange,
  onRemove,
  outOfStockMessage = 'This item is out of stock. Remove from your cart to complete your purchase.',
  removeLabel = 'Remove',
  outOfStockLabel = 'Out of Stock',
  quantityLabel = 'Qty',
  currency,
}: CartLineItemProps) => {
  const getCurrencyString = useCurrencyString(currency);

  if (!item) return null;

  return (
    <li className="border-b border-warm-200 grid gap-y-8 py-6" data-testid="cart-line-item">
      <div className="flex flex-col" role="group" aria-label="product">
        {outOfStock && (
          <div className="bg-red-400 px-4 py-2 mb-6">
            <p className="tra-text-web-body-m text-white">{outOfStockMessage}</p>
          </div>
        )}
        <div className="w-full grid gap-x-6 grid-cols-[20%_3fr_1fr]">
          <div className={classNames('relative', outOfStock && 'opacity-50')}>
            <Image
              src={item.imageUrl}
              alt={item.imageAlt}
              layout="responsive"
              width={96}
              height={96}
            />
          </div>

          <div className={classNames('w-full', outOfStock && 'opacity-50')}>
            <h5 className="text-left font-bold tra-text-web-body-l">{item.title}</h5>
            {!item.isBonus && (
              <QuantitySelector
                quantity={item.quantity}
                handleChange={(newQty) => onQuantityChange(item.id, newQty)}
                min={1}
                max={outOfStock ? 0 : 20}
                className="mt-2"
              />
            )}
            {item.isBonus && (
              <p
                className="tra-text-web-body-s text-neutral-400 font-bold"
                data-testid="quantity-label"
              >
                {quantityLabel}: {item.quantity}
              </p>
            )}
          </div>

          <div className="flex flex-col items-end justify-between sm:justify-start">
            <p className="tra-text-web-body-m text-right mb-0 flex flex-col text-neutral-500">
              {item.isBonus ? (
                <span>FREE</span>
              ) : (
                <>
                  <span>
                    <span className="sr-only">Current price: </span>
                    {getCurrencyString(item.price)}
                  </span>
                  {item.hasDiscount && item.originalPrice != null && (
                    <span className="line-through text-neutral-200">
                      <span className="sr-only">Original price: </span>
                      {getCurrencyString(item.originalPrice)}
                    </span>
                  )}
                </>
              )}
            </p>

            {!item.isBonus && (
              <div className="flex flex-col items-end">
                {outOfStock && (
                  <p className="tra-text-web-body-l overflow-hidden whitespace-nowrap bold opacity-50">
                    {outOfStockLabel}
                  </p>
                )}
                {isUpdating ? null : (
                  <button
                    className={classNames(
                      'underline tra-text-web-body-m',
                      outOfStock ? 'mt-0' : 'mt-7',
                    )}
                    aria-label={`${removeLabel} ${item.title}`}
                    data-testid="cart-remove-btn"
                    onClick={() => onRemove(item.id)}
                  >
                    {removeLabel}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </li>
  );
};

export default CartLineItem;
